import "../../../components/common/Tables/DataTable.css"
import React from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { useSearchParams } from "react-router-dom";
import ApiKeyCell from "../../../components/common/ApiKeyCell";
import SpaLink from "../../../components/common/SpaLink";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { DataTableFoot } from "../../../components/common/Tables/DataTableParts";

// Action is pinned to the right edge (px, fits Generate / Revoke)
const ACTION_WIDTH = 120;

interface TableProps {
  setGenerateOpen: any;
  setRevokeOpen: any;
  setKeyToModify: any;
  // Page controls (filters toggle) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const ManageApiKeysTable: React.FC<TableProps> = ({
  setGenerateOpen,
  setRevokeOpen,
  setKeyToModify,
  toolbar,
}) => {
  const { data: keys, loading, meta } = useSelector((state: RootState) => state.manageApiKeys);
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess: isStaff } = usePermissionCheck(['view_spa_details']);

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const total = meta?.total ?? 0;

  const handlePageChange = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  };

  const pinStyle = {'--dt-right': '0px', '--dt-w': `${ACTION_WIDTH}px`} as React.CSSProperties;

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading API keys"/></div>
        ) : keys.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                <th scope="col">API Key</th>
                <th scope="col">Spa ID</th>
                <th scope="col">Serial</th>
                <th scope="col">User</th>
                <th scope="col">Date Invoked</th>
                <th scope="col" className="dt-center dt-pin dt-pin-start" style={pinStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((row: any, i: number) => (
                <tr key={row?.apikey ?? `${row?.spaGuid}-${i}`} className={row?.revoked ? 'dt-muted-row' : undefined}>
                  <td><ApiKeyCell apiKey={row?.apikey} /></td>
                  <td>
                    <SpaLink isStaff={isStaff} spaV2Id={row?.spaV2Id} spaGuid={row?.spaGuid} label={row?.spaGuid} className="dt-guid" />
                  </td>
                  <td>
                    <SpaLink isStaff={isStaff} spaV2Id={row?.spaV2Id} spaGuid={row?.spaGuid} label={row?.spaSerial} className="dt-serial" />
                  </td>
                  <td>
                    <span className="dt-stack">
                      {row?.userName && row?.userId ? (
                        <a href={`/user/${row.userId}`} target="_blank" rel="noopener noreferrer" className="dt-link">
                          {row.userName}
                        </a>
                      ) : (
                        <span>{row?.userName ?? "—"}</span>
                      )}
                      {(row?.userEmail || row?.username) &&
                        <span className="dt-sub">
                          {[row?.userEmail, row?.username && `@${row.username}`].filter(Boolean).join(' · ')}
                        </span>}
                    </span>
                  </td>
                  <td>{row?.dateInvoked ? <span className="dt-date">{new Date(row.dateInvoked).toLocaleString()}</span> : <span className="dt-na">—</span>}</td>
                  <td className="dt-center dt-pin dt-pin-start" style={pinStyle}>
                    {row?.revoked ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary dt-row-btn"
                        onClick={() => { setKeyToModify(row); setGenerateOpen(true); }}
                      >
                        Generate
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm dt-row-btn danger"
                        onClick={() => { setKeyToModify(row); setRevokeOpen(true); }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state"><div className="dt-empty-title">No API Keys Found</div></div>
        )}
      </div>

      <DataTableFoot
        shown={keys?.length ?? 0}
        total={total}
        noun={total === 1 ? 'API Key' : 'API Keys'}
        page={page}
        totalPages={meta?.total_pages ?? 1}
        loading={loading}
        onPage={handlePageChange}
      />
    </div>
  );
};

export default ManageApiKeysTable;
